import React, { useCallback, useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { useEventListener } from '../../utils/hooks';
import { CustomButton } from '../UI/CustomButton';
import useStyles from './styles';

interface VideoPlayerProps {
  id?: string;
  src?: string;
  className?: string;
  duration?: number;
  title?: string;
  expertName?: string;
  verseId?: string | null;
  videoUrl?: string | null;
  isModal?: boolean;
  bumperVideoUrl?: string | null;
}

const exitFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  }
};

const VideoPlayer = React.forwardRef<HTMLFrameElement, VideoPlayerProps>(
  (
    {
      verseId,
      className = '',
      duration = null,
      expertName,
      title,
      id,
      videoUrl,
      isModal = false,
      bumperVideoUrl,
    },
    ref,
  ) => {
    const classes = useStyles(isModal)();
    const isVerseLoaded = useScriptLoader(SCRIPTS.VERSE);

    const [showBumper, setShowBumper] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const url = verseId
      ? `${process.env.REACT_APP_VERSE_PLAYER_URL}/stories/${verseId}-xunlocked/?embed&controls=2&sharing=0&covertitle=0`
      : videoUrl || '';

    const handleVideoWatched = useCallback(() => {
      if (bumperVideoUrl) {
        setShowBumper(true);
      }
      exitFullscreen();
    }, [bumperVideoUrl]);

    const handleRestartVideo = useCallback(
      (e) => {
        e.stopPropagation();
        // @ts-ignore
        const frame = ref?.current?.contentWindow || iframeRef?.current?.contentWindow;

        frame.postMessage({ action: 'goToChapter', index: '0' }, '*');
        setShowBumper(false);
      },
      [ref],
    );

    useEventListener('last-chapter-finished', handleVideoWatched);

    useEffect(() => {
      if (isVerseLoaded && duration && window?.verseEvents?.changeVideoDuration) {
        window.verseEvents.changeVideoDuration(duration, title, expertName);
      }
    }, [title, expertName, duration, isVerseLoaded]);

    return (
      <div className={classes.wrapper}>
        {bumperVideoUrl && (
          <div className={cx(classes.bumper, showBumper && classes.active)}>
            <video className={classes.video} autoPlay muted loop playsInline>
              <source src={bumperVideoUrl} />
            </video>
            <CustomButton onClick={handleRestartVideo} btnClass={classes.btn}>
              Restart
            </CustomButton>
          </div>
        )}
        {/*eslint-disable-next-line*/}
        <iframe
          id={id}
          className={className}
          src={url}
          frameBorder="0"
          allow="autoplay"
          allowFullScreen
          // @ts-ignore
          ref={ref || iframeRef}
        />
      </div>
    );
  },
);

export default VideoPlayer;
